import React, { useEffect, useState, useRef, useCallback } from "react";
import Card from "../ui/Card";
import ConsoleHeader from "./ConsoleHeader";
import InventoryDetails from "./InventoryDetails";
import InventoryController from "../controllers/InventoryController";
import { useSession } from "../hooks/useSession";
import GlobalController from "../controllers/GlobalController";
import HomeController from "../controllers/HomeController";
import POSController from "../controllers/POSController";
import CustomersController from "../controllers/CustomersController";
import ReconciliationController from "../controllers/ReconciliationController";
import { ROUTES } from "../utils/Routes";
import SuppliersController from "../controllers/SuppliersController";
import ReportsController from "../controllers/ReportsController";
import Label from "../ui/Label";
import Alert from "../ui/Alert";
import { pubCall, pubSub } from "../utils/PubSub";
import dayjs from "dayjs";
import OrderProcessingController from "../controllers/OrderProcessingController";
import OrdersController from "../controllers/OrdersController";
import { getSignal } from "../App";
import { showConfirm, showInfo } from "../utils/Utils";
import TestController from "../controllers/TestController";
import StyleguideController from "../controllers/StyleguideController";
import { Table } from "react-virtualized";
import Divider from "../ui/Divider";
import MainNav from "./MainNav";
import { dismissGlobalScreen } from "../hooks/createScreen";
import { MenuAccountsPayableIcon, MenuCustomersIcon, MenuHomeIcon, MenuInventoryIcon, MenuOrderProcessingIcon, MenuOrdersIcon, MenuPointOfSaleIcon, MenuReceivingIcon, MenuReportsIcon, MenuReturnsIcon, MenuSuppliersIcon } from "../utils/Icons";


export default function Console(props) {
    const { signal = {}, title = "" } = props;

    // Reconciliation Only
    let _ws_only = false;
    let flag = process.env.REACT_APP_MODE || "";

    if (flag === "recon") {
        _ws_only = true;
    }
    const WS_ONLY = _ws_only;



    // // Subscription to state
    // useEffect(() => {

    //     pubSub("bookmanager.notification", {}, (_e, _f = {}) => {

    //     }, true)
    // }, [])


    // Set the default to home
    const initialActive = WS_ONLY ? "reconciliation" : "home";
    const [active, setActive] = useState(initialActive);
    const activeRef = useRef(initialActive);


    // Need to stop scrolling when holding these keys... 
    useEffect(() => {
        const blockedKeys = new Set([
            'ArrowUp',
            'ArrowDown',
            'PageDown',
            'PageUp'
        ]);

        const handler = (e) => {
            if (blockedKeys.has(e.key)) {
                e.preventDefault();
            }
        };

        window.addEventListener('keydown', handler, {
            capture: true,
            passive: false
        });

        return () => {
            window.removeEventListener('keydown', handler, {
                capture: true
            });
        };
    }, []);


    useEffect(() => {
        const handleMove = (e) => {
            window.mouse = [e.clientY, e.clientX];
        };

        window.addEventListener("mousemove", handleMove);
        return () => window.removeEventListener("mousemove", handleMove);
    }, []);


    useEffect(() => {
        pubCall("setting.employee.getAll", {}, (_s, _r) => {
            if (_s) {
                window.employees = _r;
            }
        })
    }, [])


    const init = {
        reconciliation: (WS_ONLY) ? true : false,
        home: (WS_ONLY) ? false : true,
        orders: false,
        customers: false,
        pos: false,
        inventory: false,
        processing: false,
        suppliers: false,
        reports: false,
        test: false,
        styleguide: false,
    }

    const init_routes = {
        home: ROUTES.HOME.MENU,
        orders: ROUTES.ORDERS.ORDERS,
        customers: ROUTES.CUSTOMER.CUSTOMER,
        pos: ROUTES.POS.POS,
        inventory: ROUTES.INVENTORY.INVENTORY,
        processing: ROUTES.PROCESSING.PROCESSING,
        suppliers: ROUTES.SUPPLIERS.SUPPLIERS,
        reports: ROUTES.REPORTS.REPORTS,
        test: ROUTES.MISC.TEST,
        styleguide: ROUTES.MISC.STYLEGUIDE,
    };

    const routeMemory = useRef({ ...init_routes });



    const [mounted, setMounted] = useState({ ...init });

    const [resetKey, setResetKey] = useState(0);

    const show = useCallback((name) => {

        if (name === "returns") {
            return;
        }

        const previous = activeRef.current;
        // Remember 
        if (previous && previous !== "home") {
            routeMemory.current[previous] = window.bm_state.value;
        }

        window.ac = name;

        if (name === "home") {
            window.bm_state.value = ROUTES.HOME.MENU;
            setMounted({ ...init });
            routeMemory.current = { ...init_routes };
        } else {
            // Restore 
            window.bm_state.value = routeMemory.current[name];
        }

        setMounted((m) => m[name] ? m : { ...m, [name]: true });
        activeRef.current = name;
        setActive(name);

    }, []);


    const handleNavSelect = useCallback((name) => {
        if (dismissGlobalScreen()) {
            return;
        }

        show(name);
    }, [show]);


    useEffect(() => {
        const onSwitchController = (e) => show(e.detail);
        window.addEventListener("switchController", onSwitchController);
        return () => {
            window.removeEventListener("switchController", onSwitchController);
        }
    }, []);


    //[TEMP] SHOW ONLY RECONCILIATION
    useEffect(() => {
        window.bm_state.value = (WS_ONLY) ? ROUTES.RECONCILIATION.RECONCILIATION : ROUTES.HOME.MENU;
    }, [])


    const [isSuspended, setIsSuspended] = useState(false);

    useEffect(() => {


        if (window.credentials.store_id == 11) {
            showConfirm({
                controller: "home",
                route: window.bm_state.value,
                description: <div style={{ "textAlign": "center" }}>You are logged in as Mosaic Books - Orders are real.  <br /> Do you want to continue?</div>,
                title: "Warning",
                callback: (e) => {
                    if (!e) {
                        window.history.replaceState(null, '', '/');
                        window.location.reload();
                    }
                }
            })
        }

    }, [])


    const controllerTabs = [
        {
            key: "home",
            label: "Home",
            icon: <><MenuHomeIcon /></>,
            beta: true,
            enabled: true,
        },
        {
            key: "orders",
            label: "Orders",
            icon: <><MenuOrdersIcon /></>,
            beta: false,
            enabled: true,
        },
        {
            key: "inventory",
            label: "Inventory",
            icon: <><MenuInventoryIcon /></>,
            beta: true,
            enabled: true,
        },
        {
            key: "suppliers",
            label: "Suppliers",
            icon: <><MenuSuppliersIcon /></>,
            beta: false,
            enabled: true,
        },
        {
            key: "pos",
            label: "POS",
            icon: <><MenuPointOfSaleIcon /></>,
            beta: false,
            enabled: true,
        },
        {
            key: "returns",
            label: "Returns",
            icon: <><MenuReturnsIcon /></>,
            beta: false,
            enabled: false,
        },
        {
            key: "customers",
            label: "Customers",
            icon: <><MenuCustomersIcon /></>,
            beta: false,
            enabled: true,
        },
        {
            key: "AP",
            label: "Accounts Payable",
            icon: <><MenuAccountsPayableIcon /></>,
            beta: false,
            enabled: false,
        },

        {
            key: "processing",
            label: "Processing",
            icon: <><MenuOrderProcessingIcon /></>,
            beta: false,
            enabled: true,
        },

        {
            key: "Receiving",
            label: "Receiving",
            icon: <><MenuReceivingIcon /></>,
            beta: false,
            enabled: false,
        },

        {
            key: "reports",
            label: "Reports",
            icon: <><MenuReportsIcon /></>,
            beta: true,
            enabled: true,
        },
    ];

    return (
        <div className="ui-wrap" style={{ "width": "100%" }}>
            {isSuspended && (
                <div
                    onClick={(e) => { setIsSuspended(false); }}
                    style={{
                        position: "fixed",
                        inset: 0,
                        backgroundColor: "rgba(0,0,0,0.3)",
                        zIndex: 9999,
                        cursor: "pointer",
                    }}
                >
                </div>
            )}



            <aside className="ui-sidebar">

                <MainNav
                    controllers={(window.beta) ? controllerTabs.filter(item => item.beta) : controllerTabs}
                    mounted={mounted}
                    active={active}
                    onSelect={handleNavSelect}
                />
            </aside>

            <div className="ui-right">
                <header className="ui-header">
                    <ConsoleHeader controller="main" title="" signal={signal} />
                </header>

                <main className="ui-main">



                    {/* //[TEMP] SHOW ONLY RECONCILIATION */}
                    <GlobalController controller="global" setController={show} WS_ONLY={WS_ONLY} signal={signal} />
                    {/* <div style={{ width: 'clamp(1180px, 100vw, 1440px)', "height": "calc(100vh - 50px)", "minHeight": "598px", "margin": "0 auto" }}>
                    <ConsoleHeader title="" signal={signal} />
                    {drawController()}
                </div> */}

                    <div style={{ "height": "calc(100vh - 50px)" }}>

                        {/* Home */}
                        {WS_ONLY && mounted.reconciliation && (
                            <ReconciliationController WS_ONLY={WS_ONLY} route={ROUTES.RECONCILIATION.RECONCILIATION} setController={show} signal={signal} />
                        )}

                        {/* Home */}
                        {mounted.home && (
                            <div style={{ display: active === "home" ? "block" : "none" }}>
                                <HomeController controller="home" setController={show} signal={{ signal }} />
                            </div>
                        )}

                        {/* Orders */}
                        {mounted.orders && (
                            <div style={{ display: active === "orders" ? "block" : "none" }}>
                                <OrdersController controller="orders" setController={show} signal={signal} />
                            </div>
                        )}

                        {/* Customers */}
                        {mounted.customers && (
                            <div style={{ display: active === "customers" ? "block" : "none" }}>
                                <CustomersController controller="customers" setController={show} signal={signal} />
                            </div>
                        )}

                        {/* POS */}
                        {mounted.pos && (
                            <div style={{ display: active === "pos" ? "block" : "none" }}>
                                <POSController controller="pos" setController={show} signal={signal} />
                            </div>
                        )}

                        {/* Inventory */}
                        {mounted.inventory && (
                            <div style={{ display: active === "inventory" ? "block" : "none" }}>
                                <InventoryController controller="inventory" setController={show} signal={signal} />
                            </div>
                        )}

                        {/* Processing */}
                        {mounted.processing && (
                            <div style={{ display: active === "processing" ? "block" : "none" }}>
                                <OrderProcessingController controller="processing" setController={show} signal={signal} />
                            </div>
                        )}

                        {/* Suppliers */}
                        {mounted.suppliers && (
                            <div style={{ display: active === "suppliers" ? "block" : "none" }}>
                                <SuppliersController controller="suppliers" setController={show} signal={signal} />
                            </div>
                        )}

                        {/* Reports */}
                        {mounted.reports && (
                            <div style={{ display: active === "reports" ? "block" : "none" }}>
                                <ReportsController controller="reports" setController={show} signal={signal} />
                            </div>
                        )}

                        {/* Test */}
                        {mounted.test && (
                            <div style={{ display: active === "test" ? "block" : "none" }}>
                                <TestController controller="test" setController={show} signal={signal} />
                            </div>
                        )}

                        {/* UI Styleguide (hidden, [U] on the Home menu) */}
                        {mounted.styleguide && (
                            <div style={{ display: active === "styleguide" ? "block" : "none" }}>
                                <StyleguideController controller="styleguide" route={ROUTES.MISC.STYLEGUIDE} setController={show} signal={signal} />
                            </div>
                        )}
                    </div>

                </main>

                <footer className="ui-footer">

                </footer>
            </div>








        </div>
    )
}